import { useEffect, useMemo, useRef, useState, startTransition } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { listAuditLog } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { buildAuditListBody } from '../../constants';
import { mapAuditListRowToUi, normalizePagedMeta } from '../../utils';

const DEFAULT_META = {
  total_items: 0,
  total_pages: 1,
  current_page: 1,
  per_page: 10,
  has_next: false,
  has_previous: false,
};

/**
 * Activity log list (POST /v1/audit/list).
 */
export function useWorkspaceAuditLog({
  workspaceId,
  contextLoading,
  page,
  setPage,
  pageSize,
  debouncedSearchKeyword,
}) {
  const { t } = useTranslation('workspaces');
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState(DEFAULT_META);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const prevWorkspaceRef = useRef(workspaceId);
  const prevDebouncedSearchRef = useRef(null);

  useEffect(() => {
    if (prevDebouncedSearchRef.current === null) {
      prevDebouncedSearchRef.current = debouncedSearchKeyword;
      return;
    }
    if (prevDebouncedSearchRef.current !== debouncedSearchKeyword) {
      prevDebouncedSearchRef.current = debouncedSearchKeyword;
      startTransition(() => setPage(1));
    }
  }, [debouncedSearchKeyword, setPage]);

  useEffect(() => {
    if (!workspaceId || contextLoading) return;

    const wsChanged = prevWorkspaceRef.current !== workspaceId;
    prevWorkspaceRef.current = workspaceId;
    if (wsChanged && page !== 1) {
      startTransition(() => setPage(1));
      return;
    }

    let cancelled = false;
    startTransition(() => {
      setListLoading(true);
      setListError(null);
    });

    listAuditLog(buildAuditListBody(page, pageSize, debouncedSearchKeyword))
      .then((res) => {
        if (cancelled) return;
        const normalized = validateApiResponse(res, t('detail.activityLog.listError'));
        const rows = Array.isArray(normalized.body) ? normalized.body : [];
        setEvents(rows.map(mapAuditListRowToUi));
        setMeta(normalizePagedMeta(normalized.meta, page, pageSize));
      })
      .catch((err) => {
        if (cancelled) return;
        setListError(err?.message ?? t('common.requestFailed'));
        setEvents([]);
        setMeta({ ...DEFAULT_META, current_page: page, per_page: pageSize });
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceId, page, pageSize, contextLoading, debouncedSearchKeyword, t, setPage]);

  return useMemo(
    () => ({
      events,
      meta,
      listLoading,
      listError,
    }),
    [events, meta, listLoading, listError],
  );
}
